import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import api from '../api';
/**
 * AdminOverview — /admin
 * Platform-wide KPIs + cash flow + stuck-deposits alert + top clans.
 * Data via GET /api/admin/overview and GET /api/admin/deposits/stuck.
 */
export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const [stuck, setStuck] = useState(null);
  const [stuckErr, setStuckErr] = useState(null);
  const [creditingId, setCreditingId] = useState(null);
  const [creditMsg, setCreditMsg] = useState(null);

  const loadOverview = useCallback(() => {
    setLoading(true);
    return api.get('/admin/overview')
      .then(({ data }) => { setData(data); setLoading(false); })
      .catch((e) => { setErr(e?.response?.data?.detail || 'Failed to load overview'); setLoading(false); });
  }, []);

  const loadStuck = useCallback(() => {
    return api.get('/admin/deposits/stuck')
      .then(({ data }) => { setStuck(data); setStuckErr(null); })
      .catch((e) => setStuckErr(e?.response?.data?.detail || 'Failed to load stuck deposits'));
  }, []);

  useEffect(() => { loadOverview(); loadStuck(); }, [loadOverview, loadStuck]);

  const creditNow = async (depositId, usd, handle) => {
    if (!window.confirm(`Manually credit $${usd.toFixed(2)} to @${handle || 'this user'}?\n\nThis is irreversible and will be logged with your admin id.`)) return;
    setCreditingId(depositId);
    setCreditMsg(null);
    try {
      const { data: res } = await api.post(`/admin/deposits/${depositId}/credit-manual`);
      setCreditMsg({ ok: true, text: `Credited $${res.credited.toFixed(2)} to ${res.user_id.slice(-8)} ✓` });
      // Refresh both — the deposit disappears from stuck list, cash flow bumps up
      await Promise.all([loadOverview(), loadStuck()]);
    } catch (e) {
      setCreditMsg({ ok: false, text: e?.response?.data?.detail || 'Credit failed' });
    } finally {
      setCreditingId(null);
    }
  };

  return (
    <AdminLayout active="overview">
      <div className="admin-page" data-testid="admin-overview-page">
        <header className="admin-page-header">
          <h1 className="admin-page-title">Platform overview</h1>
          <p className="admin-page-sub">Real-time snapshot of Clanster health · updated on page load</p>
        </header>

        {loading && <div className="admin-loading" data-testid="admin-loading">Loading…</div>}
        {err && <div className="admin-error" data-testid="admin-error">{err}</div>}

        {/* Credit-now feedback — hoisted OUTSIDE stuck-deposits section so it
            stays visible even when the last stuck deposit is credited and the
            section itself unmounts. */}
        {creditMsg && (
          <div
            className={`admin-inline-msg ${creditMsg.ok ? 'ok' : 'err'}`}
            data-testid="credit-msg"
          >
            {creditMsg.text}
          </div>
        )}

        {data && (
          <>
            {/* Stuck deposits alert — surfaces BEFORE everything else when count > 0 */}
            {stuck && stuck.count > 0 && (
              <section className="admin-section" data-testid="stuck-deposits-section">
                <div className="admin-stuck-banner" data-testid="stuck-deposits-banner">
                  <div className="admin-stuck-banner-icon">⚠️</div>
                  <div className="admin-stuck-banner-body">
                    <div className="admin-stuck-banner-title">
                      {stuck.count} stuck deposit{stuck.count === 1 ? '' : 's'} awaiting manual credit
                    </div>
                    <div className="admin-stuck-banner-sub">
                      User paid on-chain (NowPayments confirmed) but our webhook failed to credit them. Review and credit below.
                    </div>
                  </div>
                </div>

                <div className="admin-table-wrap">
                  <table className="admin-table" data-testid="stuck-deposits-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th style={{ width: 120, textAlign: 'right' }}>Amount</th>
                        <th style={{ width: 90 }}>Currency</th>
                        <th>NowPayments ID</th>
                        <th style={{ width: 150 }}>Created</th>
                        <th style={{ width: 160, textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stuck.stuck_deposits.map((d) => (
                        <tr key={d.id} data-testid={`stuck-row-${d.id}`}>
                          <td>
                            <div className="admin-clan-cell">
                              <span className="admin-clan-name">{d.user_display_name || '—'}</span>
                              <span className="admin-clan-handle">@{d.user_handle || '—'}</span>
                            </div>
                            <div className="admin-kpi-sub" style={{ marginTop: 2 }}>{d.user_email}</div>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>${d.usd_amount.toFixed(2)}</td>
                          <td style={{ textTransform: 'uppercase' }}>{d.pay_currency || '—'}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.payment_id || '—'}</td>
                          <td style={{ fontSize: 12 }}>
                            {d.created_at ? new Date(d.created_at).toLocaleString() : '—'}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              className="admin-btn-primary"
                              onClick={() => creditNow(d.id, d.usd_amount, d.user_handle)}
                              disabled={creditingId === d.id}
                              data-testid={`credit-now-${d.id}`}
                            >
                              {creditingId === d.id ? 'Crediting…' : 'Credit now'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
            {stuckErr && <div className="admin-error" data-testid="stuck-error">{stuckErr}</div>}

            {/* KPI cards */}
            <div className="admin-kpi-grid" data-testid="admin-kpi-grid">
              <KpiCard
                testid="kpi-total-users"
                icon="👥"
                label="Total users"
                value={data.users.total.toLocaleString()}
                sub={`${data.users.creators.toLocaleString()} creators`}
              />
              <KpiCard
                testid="kpi-active-users"
                icon="⚡"
                label="Active (24h)"
                value={data.users.active_24h.toLocaleString()}
                sub="unique traders"
              />
              <KpiCard
                testid="kpi-volume-24h"
                icon="📈"
                label="24h volume"
                value={`${data.volume.last_24h.toLocaleString()} pts`}
                sub={`${data.volume.trades_24h.toLocaleString()} trades`}
              />
              <KpiCard
                testid="kpi-volume-all"
                icon="💹"
                label="All-time volume"
                value={`${data.volume.all_time.toLocaleString()} pts`}
                sub={`${data.volume.trades_all_time.toLocaleString()} trades`}
              />
              <KpiCard
                testid="kpi-fee-revenue"
                icon="💰"
                label="Platform revenue"
                value={`${data.fee_revenue.toLocaleString()} pts`}
                sub="5% cut on all trades"
              />
              <KpiCard
                testid="kpi-suspended"
                icon="🚫"
                label="Suspended users"
                value={data.users.suspended.toLocaleString()}
                sub="banned accounts"
                tone={data.users.suspended > 0 ? 'warn' : ''}
              />
            </div>

            {/* Cash flow — real-money in/out (crypto) */}
            {data.cash_flow && (
              <section className="admin-section" data-testid="cash-flow-section">
                <h2 className="admin-section-title">Cash flow · crypto</h2>
                <div className="admin-kpi-grid" data-testid="cash-flow-grid">
                  <KpiCard
                    testid="cf-total-deposits"
                    icon="⬇️"
                    label="Total deposits"
                    value={`$${data.cash_flow.total_deposits_usd.toLocaleString()}`}
                    sub={`${data.cash_flow.deposits_count.toLocaleString()} credited`}
                  />
                  <KpiCard
                    testid="cf-total-withdrawals-paid"
                    icon="⬆️"
                    label="Withdrawals paid"
                    value={`$${data.cash_flow.total_withdrawals_paid_usd.toLocaleString()}`}
                    sub={`${data.cash_flow.withdrawals_sent_count.toLocaleString()} sent`}
                  />
                  <KpiCard
                    testid="cf-pending-withdrawals-usd"
                    icon="⏳"
                    label="Pending withdrawals"
                    value={`$${data.cash_flow.pending_withdrawals_usd.toLocaleString()}`}
                    sub="awaiting admin approval"
                    tone={data.cash_flow.pending_withdrawals_usd > 0 ? 'warn' : ''}
                  />
                  <KpiCard
                    testid="cf-net-platform-cash"
                    icon="🏦"
                    label="Net platform cash"
                    value={`$${data.cash_flow.net_platform_cash_usd.toLocaleString()}`}
                    sub="deposits − withdrawals paid"
                    tone={data.cash_flow.net_platform_cash_usd < 0 ? 'warn' : ''}
                  />
                </div>
              </section>
            )}

            {/* Pending queues */}
            <section className="admin-section">
              <h2 className="admin-section-title">Pending queues</h2>
              <div className="admin-queue-grid" data-testid="admin-queue-grid">
                <QueueCard
                  testid="queue-withdrawals"
                  label="Pending withdrawals"
                  count={data.queues.pending_withdrawals}
                  to="/admin/withdrawals"
                  cta="Review →"
                />
                <QueueCard
                  testid="queue-verifications"
                  label="Pending verifications"
                  count={data.queues.pending_verifications}
                  to="/admin/verifications"
                  cta="Review →"
                />
                <QueueCard
                  testid="queue-stuck-deposits"
                  label="Stuck deposits"
                  count={data.queues.stuck_deposits || 0}
                  to="/admin"
                  cta={data.queues.stuck_deposits > 0 ? 'Fix above ↑' : 'All clear ✓'}
                  disabled
                />
              </div>
            </section>

            {/* Top clans */}
            <section className="admin-section">
              <h2 className="admin-section-title">Top clans · last 24h</h2>
              {data.top_clans.length === 0 ? (
                <div className="admin-empty" data-testid="admin-top-clans-empty">
                  No trades in the last 24h yet — check back soon.
                </div>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table" data-testid="admin-top-clans-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>#</th>
                        <th>Clan</th>
                        <th style={{ width: 140, textAlign: 'right' }}>Volume</th>
                        <th style={{ width: 100, textAlign: 'right' }}>Trades</th>
                        <th style={{ width: 120 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_clans.map((c, i) => (
                        <tr key={c.handle} data-testid={`top-clan-${c.handle}`}>
                          <td className="admin-table-num">{i + 1}</td>
                          <td>
                            <div className="admin-clan-cell">
                              <span className="admin-clan-name">{c.display_name}</span>
                              {c.verified && <span className="admin-verified">✓</span>}
                              <span className="admin-clan-handle">@{c.handle}</span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'right' }}>{c.volume.toLocaleString()} pts</td>
                          <td style={{ textAlign: 'right' }}>{c.trades.toLocaleString()}</td>
                          <td>
                            <Link
                              to={`/creator/${c.handle}`}
                              className="admin-inline-link"
                              data-testid={`view-clan-${c.handle}`}
                            >
                              View →
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <BatchStatsPanel />
          </>
        )}
      </div>
    </AdminLayout>
  );
}

// ---------------- Phase 2 — Batch engine analytics panel ----------------

function BatchStatsPanel() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/batch/stats')
      .then(({ data }) => { setStats(data); setErr(null); setLoading(false); })
      .catch((e) => { setErr(e?.response?.data?.detail || 'Failed to load'); setLoading(false); });
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 30s so the panel stays honest while we watch a burst.
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  if (loading && !stats) return null;
  if (err) return <div className="admin-error" data-testid="batch-stats-err">{err}</div>;
  if (!stats) return null;

  const t = stats.today || {};
  const fe = stats.friendly_errors?.counts || {};
  const feTotal = Object.values(fe).reduce((a, b) => a + (b || 0), 0);
  const batchPct = t.trades_total > 0 ? Math.round((t.batched_count / t.trades_total) * 100) : 0;

  return (
    <section className="admin-section" data-testid="admin-batch-stats">
      <div className="admin-section-header">
        <h2>🎲 Batch engine · today</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span
            className={`batch-flag-pill ${stats.flag_enabled ? 'on' : 'off'}`}
            data-testid="batch-flag-pill"
            title={`BATCH_TRADING_ENABLED=${stats.flag_enabled ? '1' : '0'}`}
          >
            {stats.flag_enabled ? '● Live' : '○ Off'}
          </span>
          <button
            onClick={load}
            className="admin-inline-link"
            data-testid="batch-stats-refresh"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className="admin-kpi-grid" style={{ marginBottom: 14 }}>
        <KpiCard
          icon="📊"
          label="Trades today"
          value={t.trades_total?.toLocaleString() || '0'}
          sub={`${batchPct}% batched · ${t.fast_path_count?.toLocaleString() || 0} fast-pathed`}
          testid="batch-kpi-total"
        />
        <KpiCard
          icon="🎲"
          label="Largest batch"
          value={t.largest_batch || 0}
          sub={t.largest_batch >= 5 ? 'Real burst hit today' : 'No large bursts yet'}
          tone={t.largest_batch >= 5 ? 'success' : undefined}
          testid="batch-kpi-largest"
        />
        <KpiCard
          icon="⏱️"
          label="Median latency"
          value={`${t.median_latency_ms?.toFixed(1) || '0.0'} ms`}
          sub={`p95 ${t.p95_latency_ms?.toFixed(0) || 0} ms · target ≤500ms`}
          tone={t.p95_latency_ms <= 550 ? 'success' : 'warn'}
          testid="batch-kpi-latency"
        />
        <KpiCard
          icon="🛡️"
          label="Friendly errors caught"
          value={feTotal}
          sub={`price-moved ${fe.price_moved || 0} · slippage ${fe.slippage || 0} · other ${(fe.over_subscribed || 0) + (fe.insufficient || 0) + (fe.other || 0)}`}
          testid="batch-kpi-errors"
        />
      </div>

      <p className="admin-section-note" data-testid="batch-stats-note">
        Batch window: <b>{stats.batch_interval_ms} ms</b> ·
        Quiet-clan fast-path after <b>{stats.batch_idle_ttl_s}s</b> idle ·
        Snapshot at {new Date(stats.as_of).toLocaleTimeString()}.
      </p>
    </section>
  );
}

function KpiCard({ icon, label, value, sub, tone, testid }) {
  return (
    <div className={`admin-kpi-card${tone ? ' tone-' + tone : ''}`} data-testid={testid}>
      <div className="admin-kpi-icon">{icon}</div>
      <div className="admin-kpi-label">{label}</div>
      <div className="admin-kpi-value">{value}</div>
      {sub && <div className="admin-kpi-sub">{sub}</div>}
    </div>
  );
}

function QueueCard({ label, count, to, disabled, cta, testid }) {
  const Wrapper = disabled ? 'div' : Link;
  const props = disabled ? {} : { to };
  return (
    <Wrapper
      {...props}
      className={`admin-queue-card${disabled ? ' disabled' : ''}${count > 0 ? ' has-items' : ''}`}
      data-testid={testid}
    >
      <div className="admin-queue-count">{count}</div>
      <div className="admin-queue-label">{label}</div>
      <div className="admin-queue-cta">{cta || 'Review →'}</div>
    </Wrapper>
  );
}
